<template>
  <v-card>
    <v-card-title class="primary">
      <v-row>
        <v-col>
          <span class="headline white--text">{{ $t(crud ? 'specifyReceiverLocation' : 'receiverLocation') }}</span>
        </v-col>
        <v-col cols="auto">
          <Icon
          icon="mdi-close"
          iconColor="white"
          :small="false"
          large
          :tooltipText="$t('close')"
          @icon-clicked="$emit('close')"/>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-row align="center">
        <v-col cols="12" class="mt-6">
          <v-card flat style="margin-bottom: -1px;">
            <div id="account-map" style="height: 65vh"></div>
          </v-card>
        </v-col>
        <v-col cols="auto">
          <v-text-field
          :readonly="!crud"
          v-model="latitudeTextField"
          :label="$t('latitude')"
          color="black"
          @blur="onTextFieldBlur"
          :rules="[rules.required, rules.decimal, rules.validLatitudeForTextBox, rules.sixDecimalPlacesOrFewer]"
          type="number"
          class="mb-n4"
          hide-spin-buttons/>
        </v-col>
        <v-col cols="auto">
          <v-text-field
          :readonly="!crud"
          v-model="longitudeTextField"
          :label="$t('longitude')"
          color="black"
          @blur="onTextFieldBlur"
          :rules="[rules.required, rules.decimal, rules.validLongitudeForTextBox, rules.sixDecimalPlacesOrFewer]"
          type="number"
          class="mb-n4"
          hide-spin-buttons/>
        </v-col>
        <v-col cols="auto" v-if="crud">
          <v-btn
          :disabled="!showApplyButton"
          class="mr-3 mt-n2 mb-n4 primary"
          @click="applyLocation">
          {{ $t('apply') }}
          </v-btn>
        </v-col>
        <v-spacer/>
        <v-col cols="12" sm="4" lg="2">
          <v-select
          :readonly="!crud"
          :append-icon="crud ? undefined : ''"
          color="black"
          :label="$t('pickupRadius')"
          :items="pickupRadiusValues"
          class="mb-n3"
          v-model="radius"
          value="value"
          @change="updatePickupRadius(spot.longitude, spot.latitude)"/>
        </v-col>
        <v-col cols="auto" v-if="!locationIsValid && crud">
          <Icon
          margin="mx-n2"
          iconColor="error"
          icon="mdi-alert"
          :small="false"
          :tooltipText="$t('mustBeUnitedStates')"/>
        </v-col>
        <v-col cols="auto" class="mb-n4" v-if="crud">
          <v-btn
          :disabled="!enableSubmitButton"
          class="primary mt-n2"
          @click="submitLocation">
            {{ $t('submit') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import * as atlas from 'azure-maps-control'
import fieldRules from '@/utils/rules'
import { getFormattedCoord, getFormattedCoordWithTrailingZeroes } from '@/utils/NumericMutations'
import { mapResizeTimeout, azureMapsApiKey, defaultCoordinates, zoomToFeature, validateCoordinatesForReceiverLocation, coordinatesInBounds, disposeMap } from '@/utils/map/MapHelpers'
import { theme } from '@/plugins/theme'
import { PickupRadius } from '@/utils/RadiusHelpers'

export default {
  name: 'AccountMap',

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    propLocation: {
      type: Object,
      default: undefined
    },
    propRadius: {
      type: Number,
      default: undefined
    },
    crud: {
      type: Boolean,
      default: true,
      required: false
    }
  },

  data: () => ({
    rules: fieldRules.rules,
    spot: {
      latitude: defaultCoordinates.latitude,
      longitude: defaultCoordinates.longitude
    },
    radius: PickupRadius.quarterMile.meters,
    marker: undefined,
    latitudeTextField: undefined,
    longitudeTextField: undefined,
    locationIsValid: true
  }),

  computed: {
    showApplyButton () {
      const latitudeText = parseFloat(this.latitudeTextField)
      const longitudeText = parseFloat(this.longitudeTextField)
      if (!coordinatesInBounds(latitudeText, longitudeText)) {
        return false
      }
      return this.spot.latitude !== latitudeText || this.spot.longitude !== longitudeText
    },

    enableSubmitButton () {
      const latitudeText = parseFloat(this.latitudeTextField)
      const longitudeText = parseFloat(this.longitudeTextField)
      if (!coordinatesInBounds(latitudeText, longitudeText)) {
        return false
      }
      const locationChangedFromProp = this.spot.latitude !== this.propLocation?.latitude ||
        this.spot.longitude !== this.propLocation?.longitude ||
        this.radius !== this.propRadius
      return locationChangedFromProp && this.locationIsValid
    },

    pickupRadiusValues () {
      return Object.values(PickupRadius).map(pr => ({
        text: pr.miles,
        value: pr.meters
      }))
    }

  },

  watch: {
    spot: {
      handler (val) {
        this.latitudeTextField = getFormattedCoordWithTrailingZeroes(val.latitude)
        this.longitudeTextField = getFormattedCoordWithTrailingZeroes(val.longitude)
      },
      deep: true
    },

    '$vuetify.breakpoint.height' () { this.onResize() },
    '$vuetify.breakpoint.width' () { this.onResize() }
  },

  async mounted () {
    if (this.propLocation?.latitude && this.propLocation?.longitude) {
      this.spot = { ...this.propLocation }
      if (this.propRadius) this.radius = this.propRadius
      this.updateCoordinateTextBoxes()
      this.locationIsValid = true
    } else {
      await this.setLocation()
    }

    setTimeout(() => {
      this.initMap()
    }, 100)
  },

  beforeDestroy () {
    if (this.map) {
      disposeMap(this.map)
      this.map = null
    }
  },

  methods: {
    submitLocation () {
      this.$emit('selected-location', { spot: this.spot, radius: this.radius })
    },

    async applyLocation () {
      const latitude = getFormattedCoordWithTrailingZeroes(this.latitudeTextField)
      const longitude = getFormattedCoordWithTrailingZeroes(this.longitudeTextField)
      if (this.rules.validLatitude(latitude) && this.rules.validLongitude(longitude)) {
        this.spot.latitude = parseFloat(latitude)
        this.spot.longitude = parseFloat(longitude)
        this.setMarkerLocation()
        this.setCamera([this.spot.longitude, this.spot.latitude])
        this.updatePickupRadius(this.spot.longitude, this.spot.latitude)
        await this.determineValidLocation()
      }
    },

    setMarkerLocation () {
      if (this.marker && this.map) {
        this.marker.setOptions({
          position: [this.spot.longitude, this.spot.latitude]
        })
      }
    },

    initMap () {
      this.map = new atlas.Map('account-map', {
        center: [this.spot.longitude, this.spot.latitude],
        zoom: 10,
        view: 'Auto',
        autoResize: true,
        style: 'satellite_road_labels',
        authOptions: {
          authType: 'subscriptionKey',
          subscriptionKey: azureMapsApiKey()
        }
      })

      this.map.events.add('ready', () => {
        this.addMarker()
      })
    },

    addMarker () {
      const position = [this.spot.longitude, this.spot.latitude]

      this.marker = new atlas.HtmlMarker({
        draggable: this.crud,
        position: position,
        pixelOffset: [0, 0],
        color: '#D15F27'
      })

      if (this.crud) {
        this.map.events.add('dragend', this.marker, (e) => {
          const coordinate = (e.target.marker._lngLat)
          this.handleDrag(coordinate)
        })
      }

      this.map.markers.add(this.marker)

      // Pickup radius circle
      const dataSource = new atlas.source.DataSource('pickup-radius')
      this.map.sources.add(dataSource)
      const point = new atlas.data.Point(position)
      const shape = new atlas.Shape(point, 'radius-shape', {
        subType: 'Circle',
        radius: this.radius
      })
      dataSource.add(shape)
      this.map.events.add('drag', this.marker, e => {
        shape.setCoordinates(atlas.data.Position.fromLatLng(e.target.marker._lngLat))
      })
      this.map.layers.add(new atlas.layer.LineLayer(dataSource, null, {
        strokeColor: theme.primary,
        strokeDashArray: [4, 4]
      }))
      this.map.layers.add(new atlas.layer.PolygonLayer(dataSource, null, {
        fillColor: theme.primary,
        fillOpacity: 0.25,
        fillPattern: 'fill-diagonal-stripes-up-red'
      }))
      zoomToFeature(shape, this.map)
    },

    updatePickupRadius (latitude, longitude) {
      const radiusSource = this.map.sources.getById('pickup-radius')
      const shape = radiusSource.getShapeById('radius-shape')
      shape.setCoordinates([latitude, longitude])
      shape.setProperties({
        ...shape.getProperties(),
        radius: this.radius
      })
    },

    async handleDrag (coordinate) {
      this.spot.latitude = parseFloat(coordinate.lat.toFixed(6))
      this.spot.longitude = parseFloat(coordinate.lng.toFixed(6))
      this.setCamera([this.spot.longitude, this.spot.latitude])
      await this.determineValidLocation()
    },

    onResize () {
      if (this.map) {
        setTimeout(() => this.map.map.resize(), mapResizeTimeout)
      }
    },

    setCamera (coordinate) {
      this.map.setCamera({
        center: coordinate,
        type: 'ease',
        duration: 350
      })
    },

    async requestLocation () {
      return new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject)
      })
    },

    async setLocation () {
      try {
        const { coords: coordinate } = await this.requestLocation()
        this.spot.latitude = getFormattedCoord(coordinate.latitude)
        this.spot.longitude = getFormattedCoord(coordinate.longitude)
      } catch (error) {
        this.spot.latitude = defaultCoordinates.latitude
        this.spot.longitude = defaultCoordinates.longitude
        this.updateCoordinateTextBoxes()
      }
    },

    updateCoordinateTextBoxes () {
      this.latitudeTextField = getFormattedCoordWithTrailingZeroes(this.spot.latitude)
      this.longitudeTextField = getFormattedCoordWithTrailingZeroes(this.spot.longitude)
    },

    onTextFieldBlur () {
      if (this.rules.validLatitudeForTextBox(this.latitudeTextField) === true || Math.abs(parseFloat(this.latitudeTextField)) <= 90) {
        this.latitudeTextField = getFormattedCoordWithTrailingZeroes(this.latitudeTextField)
      }
      if (this.rules.validLongitudeForTextBox(this.longitudeTextField) === true || Math.abs(parseFloat(this.longitudeTextField)) <= 180) {
        this.longitudeTextField = getFormattedCoordWithTrailingZeroes(this.longitudeTextField)
      }
    },

    async determineValidLocation () {
      this.locationIsValid = await validateCoordinatesForReceiverLocation({
        latitude: this.spot.latitude,
        longitude: this.spot.longitude
      })
    }
  }
}
</script>
